import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ExternalLink, FileText, MessageSquare, Search } from 'lucide-react';

import { FIREBASE_CONFIG } from '../../config/Config';
import { FS } from '../../shared/constants/firestorePaths';
import {
  fetchClientProcessIds,
  watchClientNotes,
  watchClientDocuments,
  watchLegacyDocuments,
} from '../../services/PortalProntuarioRepository.js';
import PortalPageHeader from '../../components/portal/PortalPageHeader';
import { usePortalContext } from '../../context/PortalContext';
import { logClientPortalEventOnce } from '../../utils/portalEventLog';
import { resolveActivityLabel as resolveTypeLabel } from '../../utils/portalEventLabels';
import { checkNotesIntegrity } from '../../utils/bidirectionalIntegrityGuard';

const appId = String(FIREBASE_CONFIG?.projectId || '').trim();

const normalizeLookupKey = (value) => String(value || '').trim().toLowerCase();

const formatDateTime = (raw) => {
  try {
    if (typeof raw?.toDate === 'function') {
      return raw.toDate().toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
    }
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return 'Agora';
    return date.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return 'Agora';
  }
};


const resolveEventText = (row) => {
  const payload = row?.payload && typeof row.payload === 'object' ? row.payload : {};
  const type = String(row?.type || '').trim().toUpperCase();
  if (type === 'PORTAL_UPLOAD_SUBMITTED') {
    return String(payload?.text || payload?.fileName || 'Arquivo enviado para análise').trim() || 'Arquivo enviado para análise';
  }
  if (type === 'CLIENT_DOCUMENT_VIEWED') {
    return String(payload?.text || payload?.title || 'Documento visualizado pelo cliente').trim() || 'Documento visualizado pelo cliente';
  }
  if (type === 'DOCUMENT_PUBLISHED') {
    return String(payload?.text || payload?.fileName || 'Novo documento disponível no acervo').trim() || 'Novo documento disponível no acervo';
  }
  if (type === 'DOCUMENT_CONSENT_REQUESTED') {
    return String(payload?.text || payload?.fileName || 'Novo documento publicado com exigência de consentimento informado.').trim() || 'Novo documento publicado com exigência de consentimento informado.';
  }
  if (type === 'DOC_UPLOADED') {
    return String(payload?.text || payload?.fileName || 'Documento anexado ao caso').trim() || 'Documento anexado ao caso';
  }
  if (type === 'PORTAL_UPLOAD_APPROVED') {
    return String(payload?.text || payload?.fileName || 'Arquivo aprovado e incorporado ao seu acervo').trim() || 'Arquivo aprovado e incorporado ao seu acervo';
  }
  if (type === 'PORTAL_UPLOAD_REJECTED') {
    return String(payload?.text || payload?.rejectionReason || 'Arquivo devolvido para novo envio').trim() || 'Arquivo devolvido para novo envio';
  }
  if (type === 'CLIENT_CONSENT_ACCEPTED') {
    return String(payload?.text || 'Consentimento e privacidade aceitos pelo cliente no portal.').trim() || 'Consentimento e privacidade aceitos pelo cliente no portal.';
  }
  if (type === 'CLIENT_CONSENT_DECLINED') {
    return String(payload?.text || 'Cliente recusou o consentimento e saiu do portal.').trim() || 'Cliente recusou o consentimento e saiu do portal.';
  }
  if (type === 'PETITION_CONSENT_ACCEPTED') {
    return String(payload?.text || 'Petição aprovada pelo cliente — protocolo judicial autorizado.').trim() || 'Petição aprovada pelo cliente — protocolo judicial autorizado.';
  }
  if (type === 'PETITION_CONSENT_DECLINED') {
    return String(payload?.text || 'Petição recusada pelo cliente — protocolo judicial suspenso.').trim() || 'Petição recusada pelo cliente — protocolo judicial suspenso.';
  }
  if (type === 'CLIENT_PORTAL_SESSION_STARTED') {
    return String(payload?.text || 'Cliente acessou o portal do cliente.').trim() || 'Cliente acessou o portal do cliente.';
  }
  if (type === 'CLIENT_PORTAL_PRONTUARIO_VIEWED') {
    return String(payload?.text || 'Cliente consultou o prontuário do caso.').trim() || 'Cliente consultou o prontuário do caso.';
  }
  if (type === 'CLIENT_PORTAL_PROCESSES_VIEWED') {
    return String(payload?.text || 'Cliente consultou a área de processos.').trim() || 'Cliente consultou a área de processos.';
  }
  if (type === 'CLIENT_PORTAL_FINANCE_VIEWED') {
    return String(payload?.text || 'Cliente acessou a área financeira.').trim() || 'Cliente acessou a área financeira.';
  }
  if (type === 'CLIENT_PORTAL_PROFILE_VIEWED') {
    return String(payload?.text || 'Cliente consultou a área de perfil no portal.').trim() || 'Cliente consultou a área de perfil no portal.';
  }
  if (type === 'CLIENT_PORTAL_VAULT_VIEWED') {
    return String(payload?.text || 'Cliente acessou o acervo documental no portal.').trim() || 'Cliente acessou o acervo documental no portal.';
  }
  if (type === 'PROCESS_CREATED') {
    return String(payload?.text || 'Novo processo disponibilizado ao cliente.').trim() || 'Novo processo disponibilizado ao cliente.';
  }
  if (type === 'PROCESS_UPDATED') {
    return String(payload?.text || 'Dados do processo atualizados pelo escritório.').trim() || 'Dados do processo atualizados pelo escritório.';
  }
  if (type === 'PROCESS_STATUS_UPDATED') {
    return String(payload?.text || 'Status do processo atualizado pelo escritório.').trim() || 'Status do processo atualizado pelo escritório.';
  }
  if (type === 'PROCESS_MOVEMENT_UPDATED') {
    return String(payload?.text || 'Nova movimentação processual registrada pelo escritório.').trim() || 'Nova movimentação processual registrada pelo escritório.';
  }
  if (type === 'PROCESS_MOVEMENT_ADDED') {
    return String(payload?.text || 'Novo andamento processual adicionado pelo escritório.').trim() || 'Novo andamento processual adicionado pelo escritório.';
  }
  if (type === 'PROCESS_MOVEMENT_EDITED') {
    return String(payload?.text || 'Andamento processual atualizado pelo escritório.').trim() || 'Andamento processual atualizado pelo escritório.';
  }
  if (type === 'FINANCIAL_INVOICE_CREATED') {
    return String(payload?.text || 'Nova fatura disponibilizada ao cliente no portal financeiro.').trim() || 'Nova fatura disponibilizada ao cliente no portal financeiro.';
  }
  if (type === 'FINANCIAL_ENTRY_POSTED') {
    return String(payload?.text || 'Novo lançamento financeiro registrado para o cliente.').trim() || 'Novo lançamento financeiro registrado para o cliente.';
  }
  if (type === 'FINANCIAL_ENTRY_UPDATED') {
    return String(payload?.text || 'Lançamento financeiro do cliente foi atualizado.').trim() || 'Lançamento financeiro do cliente foi atualizado.';
  }
  if (type === 'FINANCIAL_ENTRY_REMOVED') {
    return String(payload?.text || 'Lançamento financeiro do cliente foi removido.').trim() || 'Lançamento financeiro do cliente foi removido.';
  }
  if (type === 'CLIENT_FINANCIAL_PIX_COPIED') {
    return String(payload?.text || 'Cliente copiou o código Pix de uma fatura.').trim() || 'Cliente copiou o código Pix de uma fatura.';
  }
  if (type === 'CLIENT_PORTAL_PAYMENT_SIMULATED') {
    return String(payload?.text || 'Cliente simulou o pagamento de uma fatura no ambiente de teste.').trim() || 'Cliente simulou o pagamento de uma fatura no ambiente de teste.';
  }
  return String(payload?.text || row?.text || 'Sem detalhes adicionais').trim() || 'Sem detalhes adicionais';
};

const resolveActor = (row) => {
  const actor = row?.actor && typeof row.actor === 'object' ? row.actor : {};
  return String(actor?.name || '').trim() || (row?.category === 'SYSTEM' ? 'Sistema' : 'Escritório');
};

const resolveContextBadges = (row) => {
  const payload = row?.payload && typeof row.payload === 'object' ? row.payload : {};
  const badges = [];
  const workspaceLabel = String(payload?.workspaceLabel || '').trim() || String(payload?.workspaceId || '').trim();
  const relatedEntityType = String(payload?.relatedEntityType || '').trim().toUpperCase();
  const relatedEntityId = String(payload?.relatedEntityId || '').trim();

  if (workspaceLabel) badges.push(workspaceLabel);
  if (relatedEntityType === 'PROCESSO' && relatedEntityId) badges.push(`Processo ${relatedEntityId}`);
  if (relatedEntityType === 'CLIENTE' && relatedEntityId) badges.push(`Cliente ${relatedEntityId}`);

  return badges;
};

const resolveLinkedDocument = (row, docLookup) => {
  if (!docLookup || typeof docLookup !== 'object') return null;

  const payload = row?.payload && typeof row.payload === 'object' ? row.payload : {};
  const byId = docLookup?.byId && typeof docLookup.byId === 'object' ? docLookup.byId : {};
  const byName = docLookup?.byName && typeof docLookup.byName === 'object' ? docLookup.byName : {};
  const candidates = [
    payload?.documentId,
    payload?.fileName,
    payload?.title,
    row?.fileName,
    row?.title,
  ]
    .map((value) => String(value || '').trim())
    .filter(Boolean);

  for (const candidate of candidates) {
    if (byId[candidate]) return byId[candidate];
    const key = normalizeLookupKey(candidate);
    if (key && byName[key]) return byName[key];
  }

  return null;
};

const resolveOpenTarget = (row, docLookup) => {
  const payload = row?.payload && typeof row.payload === 'object' ? row.payload : {};
  const url = String(payload?.fileUrl || payload?.downloadUrl || payload?.url || '').trim();
  const documentId = String(payload?.documentId || '').trim();
  const title = String(payload?.title || payload?.fileName || resolveEventText(row) || 'Documento').trim();

  if (url) {
    return {
      href: url,
      title,
    };
  }

  if (documentId) {
    return {
      href: `/view-published?${new URLSearchParams({ documentId, title }).toString()}`,
      title,
    };
  }

  const relatedDoc = resolveLinkedDocument(row, docLookup);

  if (relatedDoc) {
    const relatedUrl = String(relatedDoc?.fileUrl || relatedDoc?.downloadUrl || relatedDoc?.url || relatedDoc?.webViewLink || '').trim();
    const relatedId = String(relatedDoc?.id || '').trim();
    const relatedTitle = String(relatedDoc?.title || relatedDoc?.name || relatedDoc?.fileName || title || 'Documento').trim();

    if (relatedUrl) {
      return {
        href: relatedUrl,
        title: relatedTitle,
      };
    }

    if (relatedId) {
      return {
        href: `/view-published?${new URLSearchParams({ documentId: relatedId, title: relatedTitle }).toString()}`,
        title: relatedTitle,
      };
    }
  }

  return null;
};

export default function PortalProntuario() {
  const { crmClient, loading: portalLoading } = usePortalContext();
  const [rows, setRows] = useState([]);
  const [linkedDocs, setLinkedDocs] = useState([]);
  const [processIds, setProcessIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialLoadDoneRef = React.useRef(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [kind, setKind] = useState('ALL');

  const clientId = useMemo(() => String(crmClient?.id || '').trim(), [crmClient?.id]);
  const emailCandidates = useMemo(() => {
    const out = [];
    const seen = new Set();
    [crmClient?.contact?.email, crmClient?.email].forEach((raw) => {
      const safe = String(raw || '').trim();
      const key = safe.toLowerCase();
      if (!safe || seen.has(key)) return;
      seen.add(key);
      out.push(safe);
    });
    return out;
  }, [crmClient?.contact?.email, crmClient?.email]);
  const clientName = useMemo(() => {
    return (
      String(crmClient?.fullName || '').trim() ||
      String(crmClient?.qualification?.physical?.socialName || '').trim() ||
      String(crmClient?.qualification?.physical?.name || '').trim() ||
      String(crmClient?.contact?.name || '').trim() ||
      'Cliente'
    );
  }, [crmClient?.fullName, crmClient?.qualification?.physical?.socialName, crmClient?.qualification?.physical?.name, crmClient?.contact?.name]);

  useEffect(() => {
    if (!clientId || loading) return;
    checkNotesIntegrity({ notes: rows, clientId });
  }, [clientId, rows, loading]);

  useEffect(() => {
    if (portalLoading) return;
    if (!clientId) return;

    (async () => {
      try {
        await logClientPortalEventOnce({
          clientId,
          type: 'CLIENT_PORTAL_PRONTUARIO_VIEWED',
          actorName: clientName,
          payload: {
            text: 'Cliente consultou o prontuário do caso.',
          },
          storage: 'session',
        });
      } catch (e) {
        void e;
      }
    })();
  }, [portalLoading, clientId, clientName]);

  useEffect(() => {
    let alive = true;

    const run = async () => {
      if (!clientId && emailCandidates.length === 0) {
        if (!alive) return;
        setProcessIds([]);
        return;
      }

      try {
        const merged = await fetchClientProcessIds(appId, clientId);
        if (!alive) return;
        setProcessIds(merged);
      } catch {
        if (!alive) return;
        setProcessIds([]);
      }
    };

    run();

    return () => {
      alive = false;
    };
  }, [clientId]);

  useEffect(() => {
    if (portalLoading) return;
    if (!clientId || !appId) {
      setRows([]);
      setError('');
      setLoading(false);
      return;
    }

    if (!initialLoadDoneRef.current) setLoading(true);
    setError('');

    const unsubscribe = watchClientNotes(
      appId,
      clientId,
      (out) => {
        setRows(out);
        setLoading(false);
        initialLoadDoneRef.current = true;
      },
      (err) => {
        setRows([]);
        setError(err);
        setLoading(false);
      }
    );

    return () => {
      try {
        unsubscribe && unsubscribe();
      } catch {
      }
    };
  }, [portalLoading, clientId]);

  useEffect(() => {
    if (portalLoading) return;
    if (!clientId) {
      setLinkedDocs([]);
      return;
    }

    let mounted = true;
    const unsubs = [];
    const sourceMaps = [];

    let recomputeTimer = null;
    const recompute = () => {
      if (!mounted) return;
      if (recomputeTimer) clearTimeout(recomputeTimer);
      recomputeTimer = setTimeout(() => {
        if (!mounted) return;
        const merged = new Map();
        sourceMaps.forEach((m) => {
          if (!(m instanceof Map)) return;
          m.forEach((value, key) => merged.set(key, value));
        });
        setLinkedDocs(Array.from(merged.values()));
      }, 150);
    };

    if (appId) {
      const unsub0 = watchClientDocuments(appId, clientId, (next) => {
        sourceMaps[0] = next;
        recompute();
      });
      unsubs.push(unsub0);
    }
    const unsub1 = watchLegacyDocuments(clientId, (next) => {
      sourceMaps[1] = next;
      recompute();
    });
    unsubs.push(unsub1);

    return () => {
      mounted = false;
      if (recomputeTimer) clearTimeout(recomputeTimer);
      unsubs.forEach((unsub) => {
        try {
          unsub && unsub();
        } catch {
        }
      });
    };
  }, [portalLoading, clientId]);

  const docLookup = useMemo(() => {
    const byId = {};
    const byName = {};

    (Array.isArray(linkedDocs) ? linkedDocs : []).forEach((docRow) => {
      const id = String(docRow?.id || '').trim();
      if (id && !byId[id]) byId[id] = docRow;

      [docRow?.title, docRow?.name, docRow?.fileName, docRow?.filename].forEach((candidate) => {
        const key = normalizeLookupKey(candidate);
        if (key && !byName[key]) byName[key] = docRow;
      });
    });

    return { byId, byName };
  }, [linkedDocs]);

  const latestPublicationVersionBySeries = useMemo(() => {
    const out = {};

    (Array.isArray(linkedDocs) ? linkedDocs : []).forEach((docRow) => {
      const seriesId = String(docRow?.publicationSeriesId || docRow?.id || '').trim();
      const version = Math.max(0, Number(docRow?.publicationVersion || 0) || 0);
      if (!seriesId || version <= 0) return;
      out[seriesId] = Math.max(out[seriesId] || 0, version);
    });

    (Array.isArray(rows) ? rows : []).forEach((row) => {
      const payload = row?.payload && typeof row.payload === 'object' ? row.payload : {};
      const seriesId = String(payload?.publicationSeriesId || payload?.documentId || '').trim();
      const version = Math.max(0, Number(payload?.publicationVersion || 0) || 0);
      if (!seriesId || version <= 0) return;
      out[seriesId] = Math.max(out[seriesId] || 0, version);
    });

    return out;
  }, [linkedDocs, rows]);

  const filteredRows = useMemo(() => {
    const term = String(search || '').trim().toLowerCase();
    return rows
      .map((row) => {
        const payload = row?.payload && typeof row.payload === 'object' ? row.payload : {};
        const linkedDoc = resolveLinkedDocument(row, docLookup);
        const documentId = String(payload?.documentId || linkedDoc?.id || '').trim();
        const publicationSeriesId = String(payload?.publicationSeriesId || linkedDoc?.publicationSeriesId || documentId || '').trim();
        const publicationVersion = Math.max(0, Number(payload?.publicationVersion || linkedDoc?.publicationVersion || 0) || 0);
        const latestPublicationVersion = publicationSeriesId ? Math.max(latestPublicationVersionBySeries[publicationSeriesId] || 0, publicationVersion) : 0;
        const isTrackedPublication = Boolean(publicationSeriesId && publicationVersion > 0);
        const isLatestPublication = Boolean(isTrackedPublication && publicationVersion >= latestPublicationVersion);
        const isHistoricalPublication = Boolean(isTrackedPublication && latestPublicationVersion > publicationVersion);

        return {
          ...row,
          documentId,
          publicationSeriesId,
          publicationVersion,
          latestPublicationVersion,
          isTrackedPublication,
          isLatestPublication,
          isHistoricalPublication,
        };
      })
      .filter((row) => {
      const type = String(row?.type || '').trim().toUpperCase();
      if (kind === 'DOCUMENTS' && !['DOCUMENT_PUBLISHED', 'DOCUMENT_CONSENT_REQUESTED', 'DOC_UPLOADED', 'CLIENT_DOCUMENT_VIEWED', 'PORTAL_UPLOAD_APPROVED'].includes(type)) {
        return false;
      }
      if (kind === 'OFFICE' && !['USER_NOTE_ADDED', 'DOCUMENT_PUBLISHED', 'DOCUMENT_CONSENT_REQUESTED', 'DOC_UPLOADED', 'PORTAL_UPLOAD_APPROVED', 'PORTAL_UPLOAD_REJECTED', 'PROCESS_CREATED', 'PROCESS_UPDATED', 'PROCESS_STATUS_UPDATED', 'PROCESS_MOVEMENT_UPDATED', 'PROCESS_MOVEMENT_ADDED', 'PROCESS_MOVEMENT_EDITED', 'FINANCIAL_INVOICE_CREATED', 'FINANCIAL_ENTRY_POSTED', 'FINANCIAL_ENTRY_UPDATED', 'FINANCIAL_ENTRY_REMOVED'].includes(type)) {
        return false;
      }
      if (kind === 'CLIENT' && !['PORTAL_UPLOAD_SUBMITTED', 'CLIENT_DOCUMENT_VIEWED', 'CLIENT_CONSENT_ACCEPTED', 'CLIENT_CONSENT_DECLINED', 'PETITION_CONSENT_ACCEPTED', 'PETITION_CONSENT_DECLINED', 'CLIENT_PORTAL_SESSION_STARTED', 'CLIENT_PORTAL_PRONTUARIO_VIEWED', 'CLIENT_PORTAL_PROCESSES_VIEWED', 'CLIENT_PORTAL_FINANCE_VIEWED', 'CLIENT_PORTAL_PROFILE_VIEWED', 'CLIENT_PORTAL_VAULT_VIEWED', 'CLIENT_FINANCIAL_PIX_COPIED', 'CLIENT_PORTAL_PAYMENT_SIMULATED'].includes(type)) {
        return false;
      }
      if (kind === 'PETITIONS' && !['PETITION_CONSENT_ACCEPTED', 'PETITION_CONSENT_DECLINED', 'PETITION_PUBLISHED'].includes(type)) {
        return false;
      }
      if (!term) return true;
      const blob = [resolveTypeLabel(row), resolveEventText(row), resolveActor(row)]
        .filter(Boolean)
        .join(' | ')
        .toLowerCase();
      return blob.includes(term);
    });
  }, [rows, search, kind, docLookup, latestPublicationVersionBySeries]);

  const openRowDocument = useCallback((row) => {
    const target = resolveOpenTarget(row, docLookup);
    if (!target?.href) return;
    try {
      window.open(target.href, '_blank', 'noopener,noreferrer');
    } catch {
      try {
        window.open(target.href, '_blank');
      } catch {
      }
    }
  }, [docLookup]);

  return (
    <div className="space-y-4">
      <PortalPageHeader
        icon={<MessageSquare className="w-5 h-5 text-[#C5A059]" />}
        title="Meu Prontuário"
        subtitle="Linha do tempo das movimentações do seu caso."
        badge="Linha do tempo"
      />

      <div className="rounded-lg border border-[#C5A059]/20 bg-[#C5A059]/10 px-3 py-2">
        <div className="text-[11px] text-[#20243B]">Publicações, devoluções, aprovações, leituras e atualizações do seu caso em ordem cronológica.</div>
      </div>

      <div className="border border-gray-200 rounded-lg bg-white overflow-hidden">
        <div className="px-3 py-2.5 border-b border-gray-200 bg-gray-50 space-y-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar no prontuário..."
              className="w-full rounded-lg border border-gray-200 bg-white pl-8 pr-3 py-1.5 text-xs text-[#20243B] outline-none focus:border-[#C5A059]"
            />
          </div>

          <div className="flex flex-wrap gap-1">
            {[
              { key: 'ALL', label: 'Tudo' },
              { key: 'OFFICE', label: 'Escritório' },
              { key: 'CLIENT', label: 'Minhas ações' },
              { key: 'DOCUMENTS', label: 'Documentos' },
              { key: 'PETITIONS', label: 'Petições' },
            ].map((item) => (
              <button key={item.key} type="button" onClick={() => setKind(item.key)}
                className={`px-2 py-0.5 rounded border text-[10px] font-semibold transition-colors ${
                  kind === item.key ? 'bg-[#20243B] text-white border-[#20243B]' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                }`}>
                {item.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="px-3 py-2 text-xs text-gray-500 portal-loading-deferred">Carregando prontuário...</div>
        ) : error ? (
          <div className="px-3 py-2 text-xs text-red-700 bg-red-50 border-b border-red-100">{error}</div>
        ) : filteredRows.length === 0 ? (
          <div className="px-3 py-3">
            <div className="text-xs font-semibold text-[#20243B]">Nenhum evento encontrado.</div>
            <div className="text-[11px] text-gray-500 mt-0.5">Novas movimentações aparecerão aqui automaticamente.</div>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {filteredRows.map((row) => {
              const openTarget = resolveOpenTarget(row, docLookup);
              const badges = resolveContextBadges(row);
              const replies = Array.isArray(row?.replies) ? row.replies : [];
              const versionBadge = row?.isTrackedPublication
                ? row?.isLatestPublication ? `v${row?.publicationVersion || 1} Atual` : `v${row?.publicationVersion || 1} Histórico`
                : '';
              return (
                <div key={row.id} className={
                  'px-3 py-2.5 ' +
                  (row?.isLatestPublication ? 'border-l-4 border-emerald-400 bg-emerald-50/30'
                    : row?.isHistoricalPublication ? 'border-l-4 border-slate-200 bg-slate-50/60 opacity-80'
                    : '')
                }>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-1 mb-0.5">
                        <span className="text-[10px] font-bold uppercase tracking-wide text-[#20243B]">{resolveTypeLabel(row)}</span>
                        {versionBadge ? (
                          <span className={`px-1 py-0 rounded text-[9px] font-bold ${
                            row?.isLatestPublication ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'
                          }`}>{versionBadge}</span>
                        ) : null}
                        <span className="px-1.5 py-0 rounded bg-gray-100 text-[9px] font-medium text-gray-600">{resolveActor(row)}</span>
                        {row?.isPinned ? <span className="px-1.5 py-0 rounded bg-[#C5A059]/10 text-[9px] font-medium text-[#7a5a16]">Fixado</span> : null}
                      </div>
                      <div className="text-xs text-gray-700 break-words">{resolveEventText(row)}</div>
                      {String(row?.payload?.rejectionReason || '').trim() ? (
                        <div className="mt-1.5 rounded border border-red-200 bg-red-50 px-2 py-1 text-[10px] text-red-700">
                          Motivo: {String(row.payload.rejectionReason)}
                        </div>
                      ) : null}
                      {replies.length ? (
                        <div className="mt-1.5 rounded border border-gray-100 bg-gray-50 px-2.5 py-1.5">
                          <div className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-wide text-gray-500 mb-1">
                            <MessageSquare className="w-3 h-3" /> Respostas
                          </div>
                          <div className="space-y-1">
                            {replies.map((reply) => (
                              <div key={String(reply?.id || `${row.id}_${reply?.at || ''}`)} className="rounded border border-gray-100 bg-white px-2 py-1">
                                <div className="flex items-center gap-2 text-[9px] text-gray-400 mb-0.5">
                                  <span>{String(reply?.by || 'Operador').trim()}</span>
                                  <span>{formatDateTime(reply?.at)}</span>
                                </div>
                                <div className="text-[11px] text-gray-700 break-words">{String(reply?.text || '').trim()}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : null}
                    </div>
                    <div className="shrink-0 text-right flex flex-col items-end gap-1">
                      <div className="text-[9px] text-gray-400 whitespace-nowrap">{formatDateTime(row?.createdAt)}</div>
                      {openTarget?.href ? (
                        <button type="button" onClick={() => openRowDocument(row)}
                          className="inline-flex items-center gap-1 rounded border border-gray-200 bg-white px-1.5 py-0.5 text-[10px] font-medium text-[#20243B] hover:bg-gray-50">
                          <ExternalLink className="w-2.5 h-2.5" /> Abrir
                        </button>
                      ) : null}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
